import React, { useEffect, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { Button, Container, Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import { ButtonType } from "@components/atoms/Button/types";
import { buttonCategory } from "./Utils";
import { useTranslation } from "react-i18next";
import DragList, { DragListRenderItemInfo } from "react-native-draglist";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ActiveSortingItemComponent from "./ActiveSortingItemComponent";
import InactiveSortingItemComponent from "./InactiveSortingItemComponent";
import {
  ascending,
  descending,
  FilterStructure,
} from "@scenes/searchResults/types";
import { useGenerateSortingOptions } from "../../hooks/useSortCustomData";
import { ProductCategory } from "@utils/constants";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Platform, useWindowDimensions } from "react-native";
import { getEmbeddedModalMaxHeight } from "@utils/iframeMessaging";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";

export type FilterProps = {
  productType: string;
  onClose: any;
  onApply: any;
  selectedSortingFilters?: Array<string>;
  sectionName?: SearchFilterTypeEnum;
};

const SortingFiltersScreen = ({
  productType,
  onClose,
  onApply,
  selectedSortingFilters,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
}: FilterProps) => {
  const { user } = useUserInfo();
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();
  const sortingOptions = useGenerateSortingOptions(
    productType as ProductCategory,
    user?.price_mode ?? 1,
    sectionName
  );
  const { bottom } = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const embeddedMaxHeight = getEmbeddedModalMaxHeight();
  const dragListMaxHeight = useMemo(
    () =>
      theme.isLargeScreen || theme.isTablet
        ? 548
        : theme.isMobile && Platform.OS === "web"
        ? Math.round((embeddedMaxHeight || windowHeight) * 0.8)
        : undefined,
    [
      theme.isLargeScreen,
      theme.isTablet,
      theme.isMobile,
      windowHeight,
      embeddedMaxHeight,
    ]
  );

  const [paramList, setParamList] = useState<string[]>([]);
  const [activeData, setActiveData] = useState<FilterStructure[]>([]);
  const [inActiveData, setInActiveData] =
    useState<FilterStructure[]>(sortingOptions);
  const [disableButton, setDisableButton] = useState<boolean>(false);

  const invertParam = (filterItem: FilterStructure) => {
    const updatedParamList = paramList.map((itemX) => {
      if (itemX === filterItem.name + " " + descending) {
        return filterItem.name + " " + ascending;
      } else if (itemX === filterItem.name + " " + ascending) {
        return filterItem.name + " " + descending;
      }
      return itemX;
    });

    setParamList(updatedParamList);
  };

  const renderItem = (info: DragListRenderItemInfo<FilterStructure>) => {
    const { item, onDragStart, onDragEnd, isActive } = info;

    return (
      <ActiveSortingItemComponent
        item={item}
        isActive={isActive}
        paramList={paramList}
        invertParam={invertParam}
        removeFromActiveOptions={removeFromActiveOptions}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        activeData={activeData}
      />
    );
  };

  useEffect(() => {
    if (
      selectedSortingFilters &&
      selectedSortingFilters.length > 0 &&
      inActiveData
    ) {
      const newActiveData: FilterStructure[] = [];
      const newParamList: string[] = [];
      const remainingInActiveData = [...inActiveData];

      selectedSortingFilters.forEach((filter) => {
        const filterAscending = remainingInActiveData.find(
          (item) => filter === item.name + " " + ascending
        );
        const filterDescending = remainingInActiveData.find(
          (item) => filter === item.name + " " + descending
        );

        if (filterAscending) {
          newParamList.push(filter);
          newActiveData.push(filterAscending);
          remainingInActiveData.splice(
            remainingInActiveData.indexOf(filterAscending),
            1
          );
        } else if (filterDescending) {
          newParamList.push(filter);
          newActiveData.push(filterDescending);
          remainingInActiveData.splice(
            remainingInActiveData.indexOf(filterDescending),
            1
          );
        }
      });

      setParamList((prevData) => [...prevData, ...newParamList]);
      setActiveData((prevData) => [...prevData, ...newActiveData]);
      setInActiveData(remainingInActiveData);
    } else if (selectedSortingFilters && selectedSortingFilters.length === 0) {
      setParamList([]);
      setActiveData([]);
    }
  }, []);

  const arraysEqual = (
    mSelectedSortingFilters: string[],
    mParamList: string[]
  ) => {
    if (mSelectedSortingFilters.length !== mParamList.length) return false;
    return mParamList?.every(
      (value, index) => value === mSelectedSortingFilters[index]
    );
  };

  useEffect(() => {
    setDisableButton(
      selectedSortingFilters
        ? arraysEqual(selectedSortingFilters, paramList)
        : false
    );
  }, [paramList]);

  useEffect(() => {
    if (activeData && activeData.length > 0) {
      var localParamList: string[];
      localParamList = [];
      activeData.map((_activeData) => {
        if (paramList.includes(_activeData.name + " " + ascending)) {
          localParamList.push(_activeData.name + " " + ascending);
        } else if (paramList.includes(_activeData.name + " " + descending)) {
          localParamList.push(_activeData.name + " " + descending);
        }
      });
      setParamList(localParamList);
    }
  }, [activeData]);

  const addToActiveOptions = (_item: FilterStructure) => {
    setInActiveData(inActiveData?.filter((itemX) => itemX !== _item));
    setActiveData((prevData) => [...prevData, _item]);
    const order = _item.default_order === "asc" ? ascending : descending;
    setParamList((prevData) => [...prevData, _item.name + " " + order]);
  };

  const removeFromActiveOptions = (_item: FilterStructure) => {
    setActiveData(activeData.filter((itemX) => itemX !== _item));
    setInActiveData((prevData) => [_item, ...prevData]);
    setParamList(
      paramList.filter(
        (itemY) =>
          itemY !== _item.name + " " + descending &&
          itemY !== _item.name + " " + ascending
      )
    );
  };

  const inActiveRenderItem = (info: FilterStructure, mIndex: number) => {
    return (
      <InactiveSortingItemComponent
        key={mIndex}
        info={info}
        mIndex={mIndex}
        inActiveData={inActiveData}
        addToActiveOptions={addToActiveOptions}
      />
    );
  };

  const onReordered = async (fromIndex: number, toIndex: number) => {
    const finalIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
    const copy = [...activeData];
    const removed = copy.splice(fromIndex, 1);

    copy.splice(finalIndex, 0, removed[0]);
    setActiveData(copy);
  };

  return (
    <>
      <Container noPadding={true}>
        <DragList
          data={activeData}
          onReordered={onReordered}
          keyExtractor={(item: FilterStructure) => item.label}
          renderItem={renderItem}
          style={[
            {
              marginBottom: bottom + 100,
              padding: 12,
              maxHeight: dragListMaxHeight,
            },
            ...(theme.isMobile && Platform.OS === "web"
              ? [{ paddingBottom: 72 }]
              : theme.isTablet
              ? [{ paddingBottom: 50 }]
              : []),
          ]}
          ListHeaderComponent={() => (
            <>
              {activeData?.length > 0 && (
                <>
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(
                      activeData?.length === 1
                        ? "sortingScreen.label.activeSortingFiltersForSingleSelection.text"
                        : "sortingScreen.label.activeSortingFilters.text"
                    )}
                  </VDBText>
                  <Spacer size={SpaceType.Double} />
                </>
              )}
            </>
          )}
          ListFooterComponent={() => (
            <>
              <Spacer size={SpaceType.Double} />
              {inActiveData && inActiveData?.length > 0 && (
                <FiltersContainer>
                  {inActiveData?.map((item, index) =>
                    inActiveRenderItem(item, index)
                  )}
                </FiltersContainer>
              )}
              {inActiveData && inActiveData?.length === 0 && (
                <Spacer size={SpaceType.Size_100} />
              )}
            </>
          )}
        />
      </Container>
      <ButtonContainer theme={theme} bottom={bottom}>
        <ButtonView>
          <Button
            testID="sorting.filters.apply"
            dataTestId="sorting.filters.apply"
            type={ButtonType.primary}
            category={buttonCategory(productType)}
            title={t("sortingScreen.button.apply.text")}
            disabled={disableButton}
            onPress={() => {
              onApply(paramList);
              onClose();
            }}
          />
        </ButtonView>
      </ButtonContainer>
    </>
  );
};

const ButtonView = styled.View`
  width: ${({ theme }) => (theme.isMobile ? "100%" : "343px")};
`;

const ButtonContainer = styled.View<any>`
  position: absolute;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme["core-01"]};
  padding: ${SpaceType.Double}px;
  padding-bottom: ${({ bottom }) => bottom + SpaceType.Double}px;
  bottom: 0;
  left: 0;
  right: 0;
  border-bottom-left-radius: ${SpaceType.Double}px;
  border-bottom-right-radius: ${SpaceType.Double}px;
`;

const FiltersContainer = styled.View`
  border-width: 1px;
  border-radius: 8px;
  padding: 2px;
  margin-bottom: ${SpaceType.Double}px;
  border-color: ${({ theme }) => theme["core-03"]};
`;

export default SortingFiltersScreen;
